/* eslint-disable react/no-array-index-key */
import React, { FC } from 'react';
import { Col, Empty, Image, Row, Skeleton, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import { TFunction } from 'i18next';

import { Business, Individual, PaymentService, Terminal, Transaction } from '~/types';
import AmountField from '~/components/fields/AmountField';
import theme from '~/theme';
import { Drawer, FxRateField, StatusField, TextField } from '~/components';
import { useApi, useMCC } from '~/hooks';
import { BusinessIcon, PersonIcon } from '~/img';
import { getPaymentDetailsQR } from '~/api/paymentDetails';
import { useOriginator } from '~/hooks/transactions';
import { useService } from '~/hooks/services';
import { getServiceParameters } from '~/utils/getServiceParameters';
import { isDisplayedEntryCategory } from '~/utils/transaction';
import { useTerminalsById } from '~/hooks/terminals';
import DeviceIcon from '~/components/DeviceIcon';
import { isEmpty } from 'lodash';
import Balances from '~/components/Balances';

interface Props {
  open: boolean;
  className?: string;
  onClose: () => void;
  data: Transaction | null;
}

const TransactionShow: FC<Props> = (props) => {
  const { open, className, data, onClose } = props;

  const [t] = useTranslation('transactions');
  const tt = (key: string): string => t(`transactions:${key}`);
  const { getMCC } = useMCC();

  const showBaseAmount =
    data?.baseAmount?.currency !== data?.amount.currency ||
    Math.abs(data?.baseAmount?.value || 0) !== Math.abs(data?.amount.value || 0);

  const acquiringEntries =
    data?.entries?.filter(
      (e: any) => e.category === 'ACQUIRING_FEE' || e.category === 'CASH_ACQUIRING_FEE'
    ) || [];

  const iAmSender = (data?.amount.value || 0) < 0;

  const exchangeRate = iAmSender
    ? data?.details?.currencyConversion?.sender?.rate
    : data?.details?.currencyConversion?.recipient?.rate;
  const { data: service } = useService(data?.details?.service?.id);

  const { data: terminalData } = useTerminalsById(data?.details?.terminal?.id);
  return (
    <Drawer
      className={`${className} TransactionShow`}
      title={
        <div>
          <div>{data?.description}</div>
          <div className="TransactionShow-Id">{`${t('translation:ID')} ${data?.id || ''}`}</div>
        </div>
      }
      onClose={onClose}
      visible={open}
    >
      {data && (
        <Space direction="vertical" size="large" style={fullWidth}>
          <Space direction="vertical" size="middle" style={fullWidth}>
            <StatusField data={data.status} />
            <TextField label={t('date')}>{moment(data.createdAt).format('LL LT')}</TextField>
            {(() => {
              switch (data.details?.paymentToken?.type) {
                case 'DYNAMIC':
                  return getTerminalFields({ data, t, getMCC, terminalData });
                case 'STATIC':
                  return getQrCodeFields({ data, t, getMCC });
                default:
                  return getServiceFields({ data, t, service });
              }
            })()}
            {(() => {
              switch (data.operationType) {
                case 'CARD_TRANSACTION':
                  return getCardTransactionFields({ data, t });
                default:
                  break;
              }
            })()}
          </Space>
          <div>
            <Typography.Title level={5}>{t('amounts')}</Typography.Title>
            <Space direction="vertical" size="middle" style={fullWidth}>
              <Space direction="vertical" size="small" style={fullWidth}>
                <TextField label={t('instructedAmount')} mode="horizontal">
                  <AmountField amount={data.amount} />
                </TextField>
                {showBaseAmount && (
                  <TextField label={t('baseAmount')} mode="horizontal">
                    <AmountField amount={data.baseAmount} />
                  </TextField>
                )}
                {acquiringEntries.map((item, index) => {
                  const { category, amount, description } = item;
                  return (
                    <TextField
                      key={index}
                      label={description === category ? t(category) : description}
                      mode="horizontal"
                    >
                      <AmountField amount={amount} />
                    </TextField>
                  );
                })}
                {exchangeRate && (
                  <TextField label={t('exchangeRate')} mode="horizontal">
                    <FxRateField
                      baseCurrency={data?.baseAmount?.currency as string}
                      quoteCurrency={data?.totalAmount.currency as string}
                      rate={exchangeRate}
                    />
                  </TextField>
                )}
              </Space>
              <Space direction="vertical" size="small" style={fullWidth}>
                {data?.entries
                  ?.filter((item) => item.category === 'TRANSACTION')
                  .map((item, index) => {
                    const { amount } = item;
                    return (
                      <TextField key={index} label={t('amount')} mode="horizontal">
                        <AmountField amount={amount} />
                      </TextField>
                    );
                  })}
                {data?.entries
                  ?.filter((item) => isDisplayedEntryCategory(item.category))
                  .map((item, index) => {
                    const { category, amount, description } = item;
                    return (
                      <TextField
                        key={index}
                        label={description === category ? tt(category) : description}
                        mode="horizontal"
                      >
                        <AmountField amount={amount} />
                      </TextField>
                    );
                  })}
                {data?.totalAmount && (
                  <TextField label={t('totalAmount')} mode="horizontal">
                    <AmountField amount={data.totalAmount} />
                  </TextField>
                )}
              </Space>
              <Space direction="vertical" size="middle" style={fullWidth}>
                <TextField label={t('postingDate')}>
                  {moment(data.postingDate).format('LL')}
                </TextField>
                <TextField label={t('postedAmount')}>
                  <AmountField amount={data.postedAmount} />
                </TextField>
                <TextField label={t('account')}>
                  {iAmSender ? data.sender.accountNumber : data.recipient.accountNumber}
                </TextField>
              </Space>
              {data?.details?.referenceId &&
                Array.isArray(data?.details?.referenceId) &&
                data?.details?.referenceId?.length > 0 && (
                  <Space direction="vertical" size="small" style={fullWidth}>
                    <Typography.Title level={5}>{t('references')}</Typography.Title>
                    {data?.details?.referenceId?.map((ref, idx) => (
                      <TextField key={ref.id || idx} label={ref.id}>
                        {ref.value}
                      </TextField>
                    ))}
                  </Space>
                )}
              {data?.details?.runningBalances && (
                <Balances balances={data?.details?.runningBalances} />
              )}
            </Space>
          </div>
        </Space>
      )}
    </Drawer>
  );
};

const getTerminalFields = (props: {
  data: Transaction;
  t: TFunction;
  getMCC: (mcc?: string) => string;
  terminalData?: Terminal;
}) => {
  const { data, t, getMCC, terminalData } = props;

  const iAmSender = (data?.amount.value || 0) < 0;
  const counterpartyAccount = iAmSender ? data.recipient.accountNumber : data.sender.accountNumber;

  return (
    <>
      <TextField label={t('counterpartyAccount')}>{counterpartyAccount}</TextField>
      <div className="DeviceCard">
        <Row wrap={false}>
          <Col flex="auto">
            <Typography.Title level={5}>{data.details?.terminal?.label}</Typography.Title>
            <Space direction="vertical" size="middle">
              <TextField label={t('amount')}>
                <AmountField amount={data.amount} />
              </TextField>
              <TextField label={t('terminal')}>{data.details?.terminal?.label}</TextField>
            </Space>
          </Col>
          <Col flex="70px" style={{ marginTop: 4 }}>
            {terminalData && !isEmpty(terminalData) && (
              <DeviceIcon
                osName={terminalData?.device?.osName}
                active={terminalData?.status === 'ACTIVE'}
                type={terminalData?.terminalType}
              />
            )}
          </Col>
        </Row>
      </div>
      {data.details?.comment && <TextField label={t('comment')}>{data.details.comment}</TextField>}
      <TextField label={t('merchantCategoryCode')}>{getMCC(data.merchantCategoryCode)}</TextField>
    </>
  );
};

const getQrCodeFields = (props: {
  data: Transaction;
  t: TFunction;
  getMCC: (mcc?: string) => string;
}) => {
  const { data, t, getMCC } = props;

  const iAmSender = (data?.amount.value || 0) < 0;
  const counterpartyAccount = iAmSender ? data.recipient.accountNumber : data.sender.accountNumber;

  return (
    <>
      <TextField label={t('counterpartyAccount')}>{counterpartyAccount}</TextField>
      <div className="DeviceCard">
        <Row wrap={false}>
          <Col flex="auto">
            <Typography.Title level={5}>{`QR ${
              data.details?.comment ? `"${data.details.comment}"` : ''
            }`}</Typography.Title>
            <Space direction="vertical" size="middle">
              <TextField label={t('amount')}>
                <AmountField amount={data.amount} />
              </TextField>
              <TextField label={t('location')}>{data.details?.location?.name}</TextField>
            </Space>
          </Col>
          <Col flex="70px" style={{ marginTop: 4 }}>
            <QrCode id={data.details?.paymentToken?.value} />
          </Col>
        </Row>
      </div>
      <TextField label={t('merchantCategoryCode')}>{getMCC(data.merchantCategoryCode)}</TextField>
    </>
  );
};

const getServiceFields = (props: { data: Transaction; t: TFunction; service?: PaymentService }) => {
  const { data, t, service: serviceInfo } = props;

  const service = data.details?.service;
  const parameters = service?.parameters;
  if (!service) return null;
  return (
    <>
      <div className="DeviceCard">
        <OriginatorTitle originator={data.originator} />
        <Space direction="vertical" size="middle">
          <TextField label={t('transferType')}>{service.name}</TextField>
          {parameters &&
            getServiceParameters(data, serviceInfo)?.map((param) => {
              return (
                param && (
                  <TextField key={param.name} label={param.name}>
                    {param.value}
                  </TextField>
                )
              );
            })}
        </Space>
      </div>
      {/* <TextField label={t('reference')}>{getMCC(data.merchantCategoryCode)}</TextField> */}
    </>
  );
};

const getCardTransactionFields = (props: { data: Transaction; t: TFunction }) => {
  const { data, t } = props;

  const maskedPan = data?.details?.cardTransaction?.card?.maskedPan;
  const terminalId = data?.details?.cardTransaction?.cardAcceptor?.terminalId;
  const merchantId = data?.details?.cardTransaction?.cardAcceptor?.merchantId;
  const merchantName = data?.details?.cardTransaction?.cardAcceptor?.name;
  const merchantCity = data?.details?.cardTransaction?.cardAcceptor?.city;
  const merchantCountry = data?.details?.cardTransaction?.cardAcceptor?.country;

  return (
    <>
      {data.details?.comment && <TextField label={t('comment')}>{data.details.comment}</TextField>}
      <div className="DeviceCard">
        <Row wrap={false}>
          <Col flex="auto">
            <Typography.Title level={5}>{t('CARD_TRANSACTION')}</Typography.Title>
            <Space direction="vertical" size="middle">
              {maskedPan && <TextField label={t('maskedPan')}>{maskedPan}</TextField>}
              {terminalId && <TextField label={t('terminalId')}>{terminalId}</TextField>}
              {merchantId && <TextField label={t('merchantId')}>{merchantId}</TextField>}
              {merchantName && <TextField label={t('merchantName')}>{merchantName}</TextField>}
              {merchantCity && <TextField label={t('merchantCity')}>{merchantCity}</TextField>}
              {merchantCountry && (
                <TextField label={t('merchantCountry')}>{merchantCountry}</TextField>
              )}
            </Space>
          </Col>
          <Col flex="70px" style={{ marginTop: 4 }}>
            <DeviceIcon active />
          </Col>
        </Row>
      </div>
    </>
  );
};

const OriginatorTitle: FC<{ originator: Transaction['originator'] }> = (props) => {
  const { originator } = props;
  const { data, isLoading, profileType } = useOriginator(originator);
  const getIcon = () => {
    switch (profileType) {
      case 'INDIVIDUAL':
        return <PersonIcon />;
      case 'BUSINESS':
        return <BusinessIcon />;
      default:
        return null;
    }
  };
  const getName = () => {
    switch (profileType) {
      case 'INDIVIDUAL':
        return `${(data as Individual)?.firstName} ${(data as Individual)?.lastName}`;
      case 'BUSINESS':
        return (data as Business).legalName;
      default:
        return null;
    }
  };
  if (isLoading) return <Skeleton.Button size="small" active />;
  return (
    <Typography.Title level={5}>
      {getName()}
      <span className="text-gray" style={{ marginLeft: 8 }}>
        {getIcon()}
      </span>
    </Typography.Title>
  );
};

const QrCode: FC<{ id: string | undefined; size?: number }> = (props) => {
  const { id, size = 70 } = props;
  const [{ data: QRdata, loading: QRloading }] = useApi(getPaymentDetailsQR(id as string), [id]);
  return (
    <>
      {QRloading && <Skeleton.Avatar shape="square" size={size} />}
      {QRdata && <Image src={URL.createObjectURL(QRdata)} alt="QR" width={size} height={size} />}
      {!QRloading && !QRdata && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </>
  );
};

const fullWidth = { width: '100%' };

export default styled(TransactionShow)`
  .TransactionShow-Id {
    font-size: 10px;
    font-weight: 500;
    color: ${theme.textColorSecondary};
    margin-top: 4px;
  }
  .TextField__horizontal {
    width: 100%;
    justify-content: space-between;
  }
`;
